// OBT — Müdür Yardımcısı paneli (F1 iskeleti + F2-A kampanya entegrasyonu)
import { useState } from 'react';
import { ClipboardList, Search, Users, Send, Megaphone, Inbox, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ObtDashboardFrame from '../ObtDashboardFrame';
import { useObtOverview, sumCampaignCounter } from '../useObtOverview';
import CampaignScreen from '../campaign/CampaignScreen';
import ReviewerWorkspace from '../review/ReviewerWorkspace';
import DistributeScreen from '../response/DistributeScreen';
import ObtReportsScreen from '../report/ObtReportsScreen';
import ObtNotificationsPanel from '../notifications/ObtNotificationsPanel';
import ObtNotificationBell from '../notifications/ObtNotificationBell';

// MY rolü, Bölge Mühendisi onayından yükselen cevapları inceler: onaylar,
// revizyon ister (BM'ye geri) veya Müdür'ün nihai kararına gönderir.
// "Cevapları İncele" ile açılır (ReviewerWorkspace role="deputy").
const TASK_CARDS = [
  {
    key: 'pending-review',
    title: 'İnceleme Bekleyen Cevaplar',
    description:
      'Bölge Mühendisi onayından yükselen cevapları inceleyin; onaylayın, revizyon isteyin veya Müdüre gönderin.',
    icon: Search,
  },
  {
    key: 'progress',
    title: 'Alt Birimlerin İlerleme Durumu',
    description: 'Bölge Mühendisi ve Formen ilerlemesini takip edin.',
    icon: Users,
  },
  {
    key: 'sent-to-director',
    title: 'Müdüre Gönderilen Sonuçlar',
    description: 'Müdür kararına iletilen cevapların durumu.',
    icon: Send,
  },
];

export default function ObtDeputyDirectorDashboard({ enabled = true }) {
  const [view, setView] = useState('overview');
  const { loading, error, campaigns, page, queueItems, reload } = useObtOverview(
    'deputy',
    { enabled: enabled && view === 'overview' }
  );

  if (view === 'campaigns') {
    return (
      <CampaignScreen enabled={enabled} onBack={() => setView('overview')} />
    );
  }

  if (view === 'review') {
    return (
      <ReviewerWorkspace
        role="deputy"
        enabled={enabled}
        onBack={() => setView('overview')}
      />
    );
  }

  if (view === 'distribute') {
    return (
      <DistributeScreen enabled={enabled} onBack={() => setView('overview')} />
    );
  }

  if (view === 'reports') {
    return (
      <ObtReportsScreen
        role="deputy"
        enabled={enabled}
        onBack={() => setView('overview')}
      />
    );
  }

  if (view === 'notifications') {
    return (
      <ObtNotificationsPanel
        role="deputy"
        enabled={enabled}
        onNavigate={(v) => setView(v)}
        onBack={() => setView('overview')}
      />
    );
  }

  const stats = [
    {
      key: 'queue',
      label: 'İncelememi bekleyen cevap',
      value: queueItems ? queueItems.length : 0,
      icon: Search,
      tone: 'warning',
      onClick: () => setView('review'),
    },
    {
      key: 'total',
      label: 'Kapsamımdaki bilgi talebi',
      value: page?.total ?? campaigns.length,
      icon: ClipboardList,
      tone: 'info',
      onClick: () => setView('campaigns'),
    },
    {
      key: 'pending-bm',
      label: 'Bölge Mühendisinde bekleyen',
      value: sumCampaignCounter(campaigns, 'pending_bm_count'),
      icon: Users,
      tone: 'warning',
      onClick: () => setView('reports'),
    },
    {
      key: 'pending-director',
      label: 'Müdür kararı bekleyen',
      value: sumCampaignCounter(campaigns, 'pending_director_count'),
      icon: Send,
      tone: 'info',
      onClick: () => setView('reports'),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-end gap-2">
        <ObtNotificationBell
          enabled={enabled && view === 'overview'}
          onClick={() => setView('notifications')}
        />
        <Button variant="outline" onClick={() => setView('reports')}>
          <BarChart3 className="mr-2 h-4 w-4" aria-hidden="true" />
          Raporlar
        </Button>
        <Button variant="outline" onClick={() => setView('distribute')}>
          <Send className="mr-2 h-4 w-4" aria-hidden="true" />
          Dağıtım Görevleri
        </Button>
        <Button variant="outline" onClick={() => setView('review')}>
          <Inbox className="mr-2 h-4 w-4" aria-hidden="true" />
          Cevapları İncele
        </Button>
        <Button onClick={() => setView('campaigns')}>
          <Megaphone className="mr-2 h-4 w-4" aria-hidden="true" />
          Bilgi Taleplerini Yönet
        </Button>
      </div>
      <ObtDashboardFrame
        title="Operasyonel Bilgi Toplama"
        description="Kapsamınızdaki saha bilgi toplama taleplerinin takibi ve incelenmesi."
        stats={stats}
        taskCards={TASK_CARDS}
        campaignsTitle="Kapsamımdaki Bilgi Talepleri"
        campaignsEmptyText="Kapsamınızda bilgi talebi bulunmuyor"
        campaigns={campaigns}
        loading={loading}
        error={error}
        onReload={reload}
        onCampaignClick={() => setView('campaigns')}
      />
    </div>
  );
}
